import { useRef, useState } from 'react';
import { capturedToDataUrl, cropCapturedImage, type CapturedImage, type CropBox } from './capture';

/**
 * A full-screen crop editor for the recipe's display picture. The user drags the
 * box to move it and the corner handles to resize it, over the source image (a
 * cookbook photo, a screenshot, or the recipe's current picture reloaded). Confirm
 * crops the region out client-side (`cropCapturedImage`) and hands back the cropped
 * `CapturedImage`; the caller stores it and uses its path as the display picture.
 *
 * Coordinates are normalized 0..1 against the *source* image, so the same box means
 * the same crop regardless of how large the image renders on screen. A box is kept
 * inside the image and never smaller than a usable slice.
 */

const MIN = 0.08;
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

type Handle = 'move' | 'nw' | 'ne' | 'sw' | 'se';

export function ImageCropEditor({
  image,
  initialBox,
  onCancel,
  onCropped,
}: {
  image: CapturedImage;
  /** Where to start the box (e.g. the importer's detected dish box); defaults to the whole image. */
  initialBox?: CropBox | null;
  onCancel: () => void;
  onCropped: (cropped: CapturedImage) => void;
}) {
  const [box, setBox] = useState<CropBox>(() => normalizeBox(initialBox));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const frameRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ handle: Handle; startX: number; startY: number; startBox: CropBox } | null>(null);

  // Pointer position as a fraction of the rendered image, clamped to it.
  const toNorm = (e: React.PointerEvent): { x: number; y: number } => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
      y: clamp((e.clientY - rect.top) / rect.height, 0, 1),
    };
  };

  const onDown = (handle: Handle) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    const p = toNorm(e);
    drag.current = { handle, startX: p.x, startY: p.y, startBox: box };
  };

  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const p = toNorm(e);
    const dx = p.x - d.startX;
    const dy = p.y - d.startY;
    setBox(applyDrag(d.handle, d.startBox, dx, dy));
  };

  const onUp = (e: React.PointerEvent) => {
    if (drag.current) (e.target as Element).releasePointerCapture?.(e.pointerId);
    drag.current = null;
  };

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      onCropped(await cropCapturedImage(image, box));
    } catch {
      setError('Deze uitsnede is te klein — maak het kader groter.');
      setBusy(false);
    }
  };

  const pct = (n: number) => `${n * 100}%`;

  return (
    <div className="crop-overlay" role="dialog" aria-modal="true" aria-label="Foto bijsnijden">
      <div className="crop-stage">
        <div className="crop-frame" ref={frameRef}>
          <img className="crop-image" src={capturedToDataUrl(image)} alt="" draggable={false} />
          <div
            className="crop-box"
            style={{ left: pct(box.x), top: pct(box.y), width: pct(box.w), height: pct(box.h) }}
            onPointerDown={onDown('move')}
            onPointerMove={onMove}
            onPointerUp={onUp}
          >
            {(['nw', 'ne', 'sw', 'se'] as const).map((h) => (
              <span
                key={h}
                className={`crop-handle ${h}`}
                onPointerDown={onDown(h)}
                onPointerMove={onMove}
                onPointerUp={onUp}
              />
            ))}
          </div>
        </div>
      </div>

      {error && <p className="sheet-note error crop-error">{error}</p>}

      <div className="crop-actions">
        <button className="btn btn-quiet" onClick={onCancel} disabled={busy}>Annuleren</button>
        <button className="btn btn-primary" onClick={() => void confirm()} disabled={busy}>
          {busy ? 'Bezig…' : 'Bijsnijden'}
        </button>
      </div>
    </div>
  );
}

/** A stored box clamped into the image, or the whole image when none is given. */
function normalizeBox(b?: CropBox | null): CropBox {
  if (!b) return { x: 0, y: 0, w: 1, h: 1 };
  const w = clamp(b.w, MIN, 1);
  const h = clamp(b.h, MIN, 1);
  return { x: clamp(b.x, 0, 1 - w), y: clamp(b.y, 0, 1 - h), w, h };
}

/** Move or resize the box by a normalized delta, keeping it inside the image and above the minimum. */
function applyDrag(handle: Handle, start: CropBox, dx: number, dy: number): CropBox {
  if (handle === 'move') {
    return {
      ...start,
      x: clamp(start.x + dx, 0, 1 - start.w),
      y: clamp(start.y + dy, 0, 1 - start.h),
    };
  }
  let { x, y, w, h } = start;
  const right = start.x + start.w;
  const bottom = start.y + start.h;
  if (handle === 'nw' || handle === 'sw') {
    x = clamp(start.x + dx, 0, right - MIN);
    w = right - x;
  }
  if (handle === 'ne' || handle === 'se') {
    w = clamp(start.w + dx, MIN, 1 - start.x);
  }
  if (handle === 'nw' || handle === 'ne') {
    y = clamp(start.y + dy, 0, bottom - MIN);
    h = bottom - y;
  }
  if (handle === 'sw' || handle === 'se') {
    h = clamp(start.h + dy, MIN, 1 - start.y);
  }
  return { x, y, w, h };
}
